import React, { useMemo, forwardRef } from "react";
import classNames from "classnames";

import { AscImageSource } from "@ascendnow/shared";

import AscTag from "../../AscTag/AscTag";
import AscImage from "../../AscImage/AscImage";
import { AscTagProps } from "../../AscTag/AscTag.types";
import styles from "./AscGenericCard.module.scss";
import PlaceHolderImage from "../../../assets/images/place-holder-image.svg";

type AscGenericCardTagType = AscTagProps | React.ReactNode;
function isAscTagProps(tag: AscGenericCardTagType): tag is AscTagProps {
  return (tag as any)?.label !== undefined && (tag as any)?.type !== undefined;
}

export interface AscGenericCardProps {
  title: string | React.ReactNode | null;
  coverImageUrl?: AscImageSource | null;
  fallbackCoverImageUrl?: AscImageSource;
  customImage?: React.ReactNode;
  tags?: AscGenericCardTagType[];
  children?: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  "data-testid"?: string;
  loading?: boolean;
  className?: string;
  tagsContainerClassName?: string;
  imageClassName?: string;
  bodyClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
}

export function renderTags(tags?: AscGenericCardTagType[]) {
  if (!tags?.length) {
    return null;
  }
  return tags?.map((tag, index) => {
    if (isAscTagProps(tag)) {
      return <AscTag key={index} {...tag} />;
    }
    return <React.Fragment key={index}>{tag}</React.Fragment>;
  });
}

const AscGenericCard = forwardRef<HTMLDivElement, AscGenericCardProps>(
  (
    {
      title,
      coverImageUrl,
      fallbackCoverImageUrl,
      customImage,
      tags,
      children,
      footer,
      onClick,
      "data-testid": dataTestId = "asc-generic-card",
      loading = false,
      className,
      tagsContainerClassName,
      imageClassName,
      bodyClassName,
      titleClassName,
      contentClassName,
      footerClassName,
    },
    ref
  ) => {
    const renderedTags = useMemo(() => renderTags(tags), [tags]);

    return (
      <div
        className={classNames(
          styles.ground,
          {
            [styles.loading]: loading,
          },
          className
        )}
        onClick={onClick}
        data-testid={dataTestId}
        ref={ref}
      >
        {tags?.length ? (
          <div
            role="marquee"
            className={classNames(styles.tagsContainer, tagsContainerClassName)}
          >
            {renderedTags}
          </div>
        ) : null}
        {customImage ? (
          customImage
        ) : coverImageUrl !== null ? (
          <AscImage
            className={classNames(styles.cardImage, imageClassName)}
            src={coverImageUrl || PlaceHolderImage}
            fallbackImageUrl={fallbackCoverImageUrl || PlaceHolderImage}
            alt={typeof title === "string" ? title : ""}
          />
        ) : null}
        <div
          className={classNames(styles.cardBody, bodyClassName)}
          data-testid="asc-card-body"
        >
          {title ? (
            typeof title === "string" ? (
              <h5 className={classNames(styles.cardTitle, titleClassName)}>
                {title}
              </h5>
            ) : (
              title
            )
          ) : null}
          {children ? (
            <div
              className={classNames(styles.cardContent, contentClassName)}
              data-testid="asc-card-content"
            >
              {children}
            </div>
          ) : null}
          {footer ? (
            <footer
              className={classNames(styles.cardFooter, footerClassName)}
              data-testid="asc-card-footer"
            >
              {footer}
            </footer>
          ) : null}
        </div>
      </div>
    );
  }
);

export default AscGenericCard;
